import React, { useState, useCallback } from 'react'
import InputMethods from './InputMethods'
import WorksheetPreview from './WorksheetPreview'
import PdfGenerator from './PdfGenerator'

/**
 * Chinese Worksheet Generator Main Component
 * Generates Traditional Chinese character practice sheets with Zhuyin, Pinyin, and stroke order
 */
const ChineseWorksheet = ({ translations = {} }) => {
  const t = (key, defaultValue) => translations[key] || defaultValue

  // State
  const [characters, setCharacters] = useState([])
  const [options, setOptions] = useState({
    showZhuyin: true,
    showPinyin: true,
    showEnglish: true,
    showTracingGuide: true,
    gridStyle: 'tianZiGe' // Options: tianZiGe, jiuGongGe, miZiGe, plain
  })
  const [isGenerating, setIsGenerating] = useState(false)

  // Handle character input from any input method
  const handleCharactersChange = useCallback((newCharacters) => {
    setCharacters(newCharacters)
  }, [])

  // Handle options toggle
  const handleOptionChange = useCallback((key) => {
    setOptions(prev => ({ ...prev, [key]: !prev[key] }))
  }, [])

  // Handle grid style change
  const handleGridStyleChange = useCallback((e) => {
    setOptions(prev => ({ ...prev, gridStyle: e.target.value }))
  }, [])

  // Handle PDF generation
  const handleGeneratePdf = useCallback(async () => {
    if (characters.length === 0) return

    setIsGenerating(true)
    try {
      // PDF generation logic will be in PdfGenerator component
      await PdfGenerator.generate(characters, options)
    } catch (error) {
      console.error('PDF generation failed:', error)
    } finally {
      setIsGenerating(false)
    }
  }, [characters, options])

  return (
    <div className="space-y-8">
      {/* Main Content - Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left Column - Input Methods */}
        <div className="space-y-6">
          <InputMethods
            onCharactersChange={handleCharactersChange}
            translations={translations}
          />

          {/* Options */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t('options_title', 'Worksheet Options')}
            </h3>

            {/* Grid Style Selector */}
            <div className="mb-4">
              <label className="block text-sm text-gray-700 mb-1.5">
                {t('grid_style_label', 'Grid Style')}
              </label>
              <select
                value={options.gridStyle}
                onChange={handleGridStyleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3366FF] focus:border-transparent"
              >
                <option value="tianZiGe">{t('grid_tianzi', '田字格 (Cross Grid)')}</option>
                <option value="jiuGongGe">{t('grid_jiugong', '九宮格 (3×3 Grid)')}</option>
                <option value="miZiGe">{t('grid_mizi', '米字格 (Rice Grid)')}</option>
                <option value="plain">{t('grid_plain', '空白格 (Plain Grid)')}</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'showZhuyin', label: t('option_zhuyin', 'Show Zhuyin') },
                { key: 'showPinyin', label: t('option_pinyin', 'Show Pinyin') },
                { key: 'showEnglish', label: t('option_english', 'Show English') },
                { key: 'showTracingGuide', label: t('option_tracing', 'Show Tracing Guide') }
              ].map(opt => (
                <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options[opt.key]}
                    onChange={() => handleOptionChange(opt.key)}
                    className="w-4 h-4 text-[#3366FF] border-gray-300 rounded focus:ring-[#3366FF]"
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGeneratePdf}
            disabled={characters.length === 0 || isGenerating}
            style={{ backgroundColor: characters.length === 0 || isGenerating ? undefined : '#3366FF' }}
            className="w-full py-3 px-4 text-white font-semibold rounded-lg hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-[#3366FF] focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
          >
            {isGenerating ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('generating', 'Generating...')}
              </span>
            ) : (
              <>
                <svg className="inline-block w-5 h-5 mr-2 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('generate_pdf', 'Generate PDF Worksheet')}
              </>
            )}
          </button>
        </div>

        {/* Right Column - Preview */}
        <div>
          <WorksheetPreview
            characters={characters}
            options={options}
            translations={translations}
          />
        </div>
      </div>
    </div>
  )
}

export default ChineseWorksheet
